/**
 * Provides a centralized monitoring dashboard for tracking data refresh
 * health across application widgets and data sources. Displays refresh
 * schedules, refresh status indicators, error conditions, and operational
 * alerts to help administrators identify overdue refreshes, monitor data
 * availability, and ensure dashboard information remains current and reliable.
 */
import React, { useMemo } from "react";
import {
  AlertTriangle,
  Bell01,
  CheckCircle,
  Database01,
  Calendar,
} from "@untitledui/icons";

interface RefreshItem {
  widgetName: string;
  tableName: string;
  lastRefreshDate: string | null;
  nextRefreshDate: string | null;
  hasError: boolean;
  errorMessage: string | null;
}

interface Props {
  items: RefreshItem[];
}

const DataRefreshCenter: React.FC<Props> = ({ items }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatDate = (date: string | null) => {
    if (!date) return "--";

    return new Date(date).toLocaleDateString("en-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatus = (nextRefreshDate: string | null) => {
    if (!nextRefreshDate) {
      return {
        status: "Unknown",
        color: "bg-gray-50 text-gray-700 border-gray-200",
        icon: AlertTriangle,
        diff: 0,
        priority: 4,
      };
    }

    const next = new Date(nextRefreshDate);
    next.setHours(0, 0, 0, 0);

    const diffDays = Math.ceil(
      (next.getTime() - today.getTime()) /
        (1000 * 60 * 60 * 24)
    );

    if (diffDays < 0) {
      return {
        status: "Overdue",
        color: "bg-red-50 text-red-700 border-red-200",
        icon: AlertTriangle,
        diff: Math.abs(diffDays),
        priority: 1,
      };
    }

    if (diffDays === 0) {
      return {
        status: "Due Today",
        color:
          "bg-orange-50 text-orange-700 border-orange-200",
        icon: Bell01,
        diff: 0,
        priority: 2,
      };
    }

    return {
      status: "Healthy",
      color:
        "bg-green-50 text-green-700 border-green-200",
      icon: CheckCircle,
      diff: diffDays,
      priority: 3,
    };
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      if (a.hasError && !b.hasError) return -1;
      if (!a.hasError && b.hasError) return 1;

      return (
        getStatus(a.nextRefreshDate).priority -
        getStatus(b.nextRefreshDate).priority
      );
    });
  }, [items]);

  const summary = useMemo(() => {
    return items.reduce<{
      overdue: number;
      dueToday: number;
      healthy: number;
      error: number;
    }>(
      (acc, item) => {
        const status = getStatus(
          item.nextRefreshDate
        );

        if (item.hasError) {
          acc.error++;
        } else if (status.status === "Overdue") {
          acc.overdue++;
        } else if (status.status === "Due Today") {
          acc.dueToday++;
        } else {
          acc.healthy++;
        }

        return acc;
      },
      {
        overdue: 0,
        dueToday: 0,
        healthy: 0,
        error: 0,
      }
    );
  }, [items]);

  const attentionCount =
    summary.overdue +
    summary.dueToday +
    summary.error;

  return (
    <div className="bg-[#f6f4ef] min-h-screen p-5">
      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-200 px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Data Refresh Center
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                Monitor refresh schedules across
                Daraja widgets.
              </p>
            </div>

            {attentionCount > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 border border-red-200">
                <Bell01
                  size={18}
                  className="text-red-600"
                />

                <span className="text-sm font-medium text-red-700">
                  {attentionCount} widget
                  {attentionCount > 1 ? "s" : ""}{" "}
                  require attention
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="p-5">
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-sm text-red-600">
                Overdue
              </div>

              <div className="text-2xl font-semibold text-red-700 mt-1">
                {summary.overdue}
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="text-sm text-orange-600">
                Due Today
              </div>

              <div className="text-2xl font-semibold text-orange-700 mt-1">
                {summary.dueToday}
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm text-green-600">
                Healthy
              </div>

              <div className="text-2xl font-semibold text-green-700 mt-1">
                {summary.healthy}
              </div>
            </div>

            <div className="bg-red-100 border border-red-300 rounded-lg p-4">
              <div className="text-sm text-red-700">
                Table Errors
              </div>

              <div className="text-2xl font-semibold text-red-800 mt-1">
                {summary.error}
              </div>
            </div>
          </div>

          {/* Widget Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {sortedItems.map((item) => {
              if (item.hasError) {
                return (
                  <div
                    key={`${item.widgetName}-${item.tableName}`}
                    className="bg-white border border-red-200 rounded-xl"
                  >
                    <div className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">
                            {item.widgetName}
                          </h3>

                          <div className="flex items-center gap-1 mt-2 text-xs text-gray-500 break-all">
                            <Database01 size={14} />
                            {item.tableName}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 px-2.5 py-1 rounded-full border border-red-200 bg-red-50 text-red-700 text-xs font-medium">
                          <AlertTriangle size={14} />
                          Error
                        </div>
                      </div>

                      <div className="mt-5 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <AlertTriangle
                            size={18}
                            className="text-red-600"
                          />

                          <span className="font-medium text-red-700">
                            Table Not Available
                          </span>
                        </div>

                        <p className="mt-2 text-sm text-red-600">
                          {item.errorMessage ||
                            "Unable to retrieve refresh information."}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }

              const status = getStatus(
                item.nextRefreshDate
              );

              const StatusIcon = status.icon;

              return (
                <div
                  key={`${item.widgetName}-${item.tableName}`}
                  className="bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all duration-200"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {item.widgetName}
                        </h3>

                        <div className="flex items-center gap-1 mt-2 text-xs text-gray-500 break-all">
                          <Database01 size={14} />
                          {item.tableName}
                        </div>
                      </div>

                      <div
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium ${status.color}`}
                      >
                        <StatusIcon size={14} />
                        {status.status}
                      </div>
                    </div>

                    <div className="mt-5 space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">
                          Last Refresh
                        </span>

                        <span className="font-medium text-gray-900">
                          {formatDate(
                            item.lastRefreshDate
                          )}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">
                          Next Refresh
                        </span>

                        <span className="font-medium text-gray-900">
                          {formatDate(
                            item.nextRefreshDate
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 bg-gray-50 rounded-lg p-3 text-sm">
                      {status.status === "Healthy" && (
                        <span className="text-green-700">
                          Refresh scheduled in{" "}
                          <strong>
                            {status.diff} day
                            {status.diff !== 1
                              ? "s"
                              : ""}
                          </strong>
                        </span>
                      )}

                      {status.status === "Due Today" && (
                        <span className="text-orange-700">
                          Refresh is required today.
                        </span>
                      )}

                      {status.status === "Overdue" && (
                        <span className="text-red-700">
                          Refresh overdue by{" "}
                          <strong>
                            {status.diff} day
                            {status.diff !== 1
                              ? "s"
                              : ""}
                          </strong>
                          .
                        </span>
                      )}
                    </div>

                    {(status.status === "Due Today" ||
                      status.status === "Overdue") && (
                      <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <Calendar
                          size={16}
                          className="text-red-600"
                        />

                        <span className="text-xs font-medium text-red-700">
                          Administrator action required
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Database01
                size={40}
                className="text-gray-300 mb-3"
              />

              <p className="text-sm text-gray-500">
                No refresh information available.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataRefreshCenter;